/**
 * Client-side image compression utility.
 *
 * Mobile camera photos (iPhone HEIC/JPEG) are typically 5-15MB which causes
 * very slow uploads and even crashes the page on iOS Safari when decoded
 * via FileReader (large base64 strings freeze the JS thread, producing
 * the black-screen / flash effect users report).
 *
 * Strategy:
 *   1. Decode via createImageBitmap when available (off main thread on iOS 15+).
 *   2. Downscale to maxDimension on the longest side using a canvas.
 *   3. Re-encode as JPEG at given quality.
 *   4. Carefully release ObjectURL / ImageBitmap to free memory between
 *      multiple photo uploads (prevents iOS killing the page).
 *
 * Returns a File ready to be sent through FormData.
 */

const isImage = (file) => file && typeof file.type === "string" && file.type.startsWith("image/");

const decodeImage = async (file) => {
  // Prefer createImageBitmap (faster, no full base64 load)
  if (typeof createImageBitmap === "function") {
    try {
      return { type: "bitmap", img: await createImageBitmap(file) };
    } catch {
      // Some browsers fail on HEIC – fall through to <img> path
    }
  }
  // Fallback: object URL + <img>
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("image decode"));
      i.src = url;
    });
    return { type: "img", img, url };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
};

const releaseDecoded = (decoded) => {
  if (!decoded) return;
  if (decoded.type === "bitmap" && decoded.img && decoded.img.close) {
    try { decoded.img.close(); } catch { /* noop */ }
  }
  if (decoded.type === "img" && decoded.url) {
    try { URL.revokeObjectURL(decoded.url); } catch { /* noop */ }
  }
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), type, quality);
    } else {
      // Old Safari fallback
      const dataUrl = canvas.toDataURL(type, quality);
      const bin = atob(dataUrl.split(",")[1]);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      resolve(new Blob([arr], { type }));
    }
  });

/**
 * Compress an image file.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number, mimeType?: string, skipBelowKB?: number }} opts
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  const {
    maxDimension = 1920,
    quality = 0.8,
    mimeType = "image/jpeg",
    skipBelowKB = 400, // already small? don't waste CPU
  } = opts;

  if (!file) return file;
  if (!isImage(file)) return file; // PDFs etc. pass through
  if (file.size <= skipBelowKB * 1024 && file.type === mimeType) return file;

  let decoded;
  try {
    decoded = await decodeImage(file);
    const { img } = decoded;
    const sw = img.width || img.naturalWidth;
    const sh = img.height || img.naturalHeight;
    if (!sw || !sh) return file;

    const scale = Math.min(1, maxDimension / Math.max(sw, sh));
    const dw = Math.round(sw * scale);
    const dh = Math.round(sh * scale);

    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d");
    // White background so transparent PNGs don't become black in JPEG
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, dw, dh);
    ctx.drawImage(img, 0, 0, dw, dh);

    const blob = await canvasToBlob(canvas, mimeType, quality);
    if (!blob) return file;
    if (blob.size >= file.size) return file; // compression actually made it bigger -> keep original

    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: mimeType, lastModified: Date.now() });
  } catch {
    return file; // any failure -> upload original
  } finally {
    releaseDecoded(decoded);
  }
}
